import type {
  AccessibilitySettings,
  AdminDashboard,
  BetPosition,
  MarketDirection,
  PlayerProfile,
  PlayerSession,
  PoolSnapshot,
  QuestClaim,
  RaidState,
  ReferralProgress,
  RoundState,
  SocialQuest,
  SpeciesDefinition,
  WalletSnapshot,
} from '../shared/contracts'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 8_000)
  try {
    const response = await fetch(path, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
    const body = await response.json() as T & { error?: string; code?: string }
    if (!response.ok) throw new ApiError(body.error ?? 'The current pushed back.', body.code ?? 'UNKNOWN', response.status)
    return body
  } finally {
    window.clearTimeout(timeout)
  }
}

export const api = {
  me: () => request<PlayerSession | null>('/api/me'),
  wallet: () => request<WalletSnapshot>('/api/wallet'),
  economy: () => request<PoolSnapshot>('/api/economy'),
  species: () => request<SpeciesDefinition[]>('/api/species'),
  raid: () => request<RaidState>('/api/raid'),
  bets: (status: 'open' | 'settled') => request<BetPosition[]>(`/api/bets?status=${status}`),
  quests: () => request<SocialQuest[]>('/api/quests'),
  questClaims: () => request<QuestClaim[]>('/api/quests/claims'),
  submitQuest: (questId: string, proofUrl: string, note: string) => request<QuestClaim>(
    `/api/quests/${questId}/claims`,
    { method: 'POST', body: JSON.stringify({ proofUrl, note }) },
  ),
  referral: () => request<ReferralProgress>('/api/referrals/me'),
  captureReferral: (code: string) => request<{ captured: true }>(
    '/api/referrals/capture',
    { method: 'POST', body: JSON.stringify({ code }) },
  ),
  changeTemporaryPassword: (currentPassword: string, newPassword: string) => request<{ updated: true }>(
    '/api/account/password-change-complete',
    { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) },
  ),
  resendVerification: (email: string, callbackURL: string) => request<{ sent: boolean; cooldown: boolean; nextAllowedAt?: number }>(
    '/api/account/resend-verification',
    { method: 'POST', body: JSON.stringify({ email, callbackURL }) },
  ),
  createRound: (mode: 'deep-rush' | 'classic-swim') => request<RoundState>(
    '/api/rounds',
    { method: 'POST', body: JSON.stringify({ mode }) },
  ),
  predict: (roundId: string, input: { bandIndex?: number; tileId?: string; stake: string }) => request<RoundState>(
    `/api/rounds/${roundId}/prediction`,
    { method: 'POST', body: JSON.stringify(input) },
  ),
  shift: (roundId: string, direction: -1 | 1) => request<RoundState>(
    `/api/rounds/${roundId}/shift`,
    { method: 'POST', body: JSON.stringify({ direction }) },
  ),
  joinRaid: () => request<RaidState>('/api/raid/join', { method: 'POST', body: '{}' }),
  raidPredict: (checkpoint: 0 | 1 | 2, direction: MarketDirection) => request<RaidState>(
    '/api/raid/predict',
    { method: 'POST', body: JSON.stringify({ checkpoint, direction }) },
  ),
  settings: (settings: Partial<AccessibilitySettings>) => request<PlayerProfile>(
    '/api/settings',
    { method: 'PATCH', body: JSON.stringify(settings) },
  ),
  adminDashboard: () => request<AdminDashboard>('/api/admin/dashboard'),
  adminCreateUser: (input: { name: string; email: string; temporaryPassword: string; role: 'user' | 'admin' }) => request<{ userId: string }>(
    '/api/admin/users',
    { method: 'POST', body: JSON.stringify(input) },
  ),
  adminBanUser: (userId: string, banned: boolean, reason?: string) => request<{ updated: true }>(
    `/api/admin/users/${userId}/ban`,
    { method: 'POST', body: JSON.stringify({ banned, reason }) },
  ),
  adminResetPassword: (userId: string, temporaryPassword: string) => request<{ updated: true }>(
    `/api/admin/users/${userId}/reset-password`,
    { method: 'POST', body: JSON.stringify({ temporaryPassword }) },
  ),
  adminAdjustWallet: (input: { userId: string; asset: 'USD_VIRTUAL' | 'SWIM'; amount: string; reason: string; idempotencyKey: string }) => request<WalletSnapshot>(
    '/api/admin/wallet-adjustments',
    { method: 'POST', body: JSON.stringify(input) },
  ),
  adminAdjustPool: (input: { poolId: string; amount: string; reason: string; idempotencyKey: string }) => request<{ updated: true }>(
    '/api/admin/pool-adjustments',
    { method: 'POST', body: JSON.stringify(input) },
  ),
  adminSetSetting: (key: string, value: number) => request<{ updated: true }>(
    `/api/admin/settings/${key}`,
    { method: 'PUT', body: JSON.stringify({ value }) },
  ),
  adminClearSetting: (key: string) => request<{ updated: true }>(
    `/api/admin/settings/${key}`,
    { method: 'DELETE' },
  ),
  adminCreateQuest: (input: { title: string; description: string; instructions: string; rewardSwim: string; startsAt: number; endsAt: number }) => request<SocialQuest>(
    '/api/admin/quests',
    { method: 'POST', body: JSON.stringify(input) },
  ),
  adminReviewQuestClaim: (claimId: string, decision: 'approved' | 'rejected', reviewerNote: string) => request<QuestClaim>(
    `/api/admin/quest-claims/${claimId}/review`,
    { method: 'POST', body: JSON.stringify({ decision, reviewerNote }) },
  ),
}

export function socketUrl(): string {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${location.host}/ws`
}
